/* eslint-disable styled-components-a11y/click-events-have-key-events */
/* eslint-disable styled-components-a11y/no-static-element-interactions */
import React, { useState, type MouseEvent } from 'react';
import styled, { css } from 'styled-components';

import { pxToRem } from '@yoweb/ui/styles/utils';
import { ButtonIcon, type ButtonIconProps } from '@yoweb/ui/components/buttons/ButtonIcon';
import { getDuration, getFontSize, getFontWeight, th } from '@yoweb/ui/styles/utils/theme';
import type { ButtonMode } from '@yoweb/ui/components/buttons/Button';

type ButtonIconWithHoverLabelProps = ButtonIconProps & {
  hoverLabel: string;
  hasShadow?: boolean;
  modeTo?: ButtonMode;
};

export const ButtonIconWithHoverLabel = ({
  hoverLabel,
  mode,
  modeTo = 'dark',
  hasShadow,
  onClick,
  onMouseEnter,
  onMouseLeave,
  ...props
}: ButtonIconWithHoverLabelProps) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = (e: MouseEvent<HTMLElement>) => {
    setIsHovered(true);
    if (onMouseEnter) {
      onMouseEnter(e as MouseEvent<HTMLButtonElement, globalThis.MouseEvent>);
    }
  };

  const handleMouseLeave = (e: MouseEvent<HTMLElement>) => {
    setIsHovered(false);
    if (onMouseLeave) {
      onMouseLeave(e as MouseEvent<HTMLButtonElement, globalThis.MouseEvent>);
    }
  };

  return (
    <ButtonIconContainer
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={onClick}
    >
      <ButtonIconContent hasShadow={hasShadow}>
        <ButtonIcon {...props} mode={isHovered ? modeTo ?? mode : mode} />
        <ButtonTextWrapper>{hoverLabel}</ButtonTextWrapper>
      </ButtonIconContent>
    </ButtonIconContainer>
  );
};

const ButtonIconContainer = styled.div``;

const ButtonIconContent = styled.span<{ hasShadow?: boolean }>`
  background-color: ${th.getColor('base900')};
  color: ${th.getColor('base000')};
  border: ${pxToRem(1)} solid;
  border-color: ${th.getColor('base000')};
  border-radius: ${pxToRem(50)};
  padding: ${pxToRem(8)};
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  ${({ hasShadow }) =>
    hasShadow &&
    css`
      box-shadow: ${pxToRem(0)} ${pxToRem(1)} 30px ${th.getColor('base700')};
    `}
`;

const ButtonTextWrapper = styled.div`
  justify-content: center;
  align-items: center;
  overflow-x: hidden;
  white-space: nowrap;
  font-weight: ${getFontWeight('semibold')};
  font-size: ${getFontSize('bodyLg')};
  max-width: ${pxToRem(0)};
  opacity: 0;
  padding: 0;
  transition:
    max-width ${getDuration('interaction')}ms ease-in-out,
    opacity ${getDuration('interaction')}ms ease-in-out,
    transform ${getDuration('interaction')}ms ease-in-out;
  transform: translateX(${pxToRem(10)});

  ${ButtonIconContainer}:hover & {
    max-width: ${pxToRem(120)};
    opacity: 1;
    transform: 0;
    padding-right: ${pxToRem(16)};
  }
`;
